import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { useAuth } from '../AuthContext'
import { ApiError } from '../api/client'
import {
  MEDICATION_CURATION_KEY,
  fetchMedicationCuration,
  sendOffer,
  type CurationRequest,
  type CurationResponse,
} from '../api/medications'
import { Callout } from '../components/Callout'
import { EmptyState } from '../components/EmptyState'
import { FilteredEmpty } from '../components/FilteredEmpty'
import { useFilterQuery } from '../TableFilterContext'
import { RecordCard, type Fact } from '../components/RecordCard'
import { compactAge } from '../api/dashboard'
import { formatPesewas } from '../../lib/money'

const URGENCY_LABEL: Record<string, string> = {
  same_day: 'Same day',
  '24h': 'Within 24 hrs',
  routine: 'Routine',
}

/**
 * AMTC medication curation (M12-T06b, ADR-0028). amtc_admin only. Each active
 * request lists its pharmacy answers **pre-ranked** (price → distance → time);
 * the concierge sends ONE as the curated offer (pickup) — the patient never sees
 * the list. Sending texts the patient and is audit-logged.
 */
export function MedicationCurationPage(): ReactNode {
  const { token } = useAuth()
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: MEDICATION_CURATION_KEY,
    queryFn: () => fetchMedicationCuration(token),
    enabled: Boolean(token),
  })

  // Client filter, `false` for "not paginated" (M25-T01). Like the pharmacy
  // queue, `list_curation_requests` carries no `LIMIT` and is drained by
  // `status IN ('pending','offered') AND expires_at > now()`, so these cards
  // are the whole queue. No `SEARCHABLE` entry, deliberately.
  const query = useFilterQuery(false)
  const term = query.trim().toLowerCase()
  const visible =
    data === undefined || term === ''
      ? data
      : data.filter((r) =>
          [r.reference, r.patient_name, r.drug_generic_name, r.drug_brand_name, r.area]
            .some((f) => f?.toLowerCase().includes(term)),
        )

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h1 className="font-display text-2xl font-semibold text-ink-1">
          Medication
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-3">
          Answers are pre-ranked on price, distance and urgency. Send one as the
          patient&apos;s offer — they see only your pick, never the list. Every
          action is audit-logged.
        </p>
      </div>

      {isPending ? (
        <div
          role="status"
          aria-label="Loading curation queue"
          className="h-40 w-full max-w-3xl animate-pulse rounded-2xl bg-bg-sunken"
        />
      ) : isError ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-4 rounded-[10px] border border-bed-bad/40 bg-bed-bad-bg px-4 py-3"
        >
          <p className="text-sm font-medium text-bed-bad">
            {error instanceof ApiError
              ? error.message
              : 'Could not load the curation queue.'}
          </p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="inline-flex h-8 items-center justify-center rounded-[8px] border border-line-2 bg-bg-app px-3 text-sm font-medium text-ink-1 hover:bg-bg-soft"
          >
            Retry
          </button>
        </div>
      ) : data.length === 0 ? (
        <div className="rounded-2xl border border-line-1">
          <EmptyState title="No active medication requests.">
            Requests appear here as patients ask for a drug, and expire after 24
            hours.
          </EmptyState>
        </div>
      ) : visible !== undefined && visible.length === 0 ? (
        // Filtered to nothing is not the same as having nothing. No second
        // line: this filter covered the whole queue.
        <div className="rounded-2xl border border-line-1">
          <FilteredEmpty query={query} />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {(visible ?? []).map((r) => (
            <RequestCard key={r.reference} request={r} />
          ))}
        </div>
      )}
    </section>
  )
}

function RequestCard({ request }: { request: CurationRequest }): ReactNode {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  // The row the concierge has clicked but not yet confirmed. Sending an offer
  // texts a patient, so the click ARMS it and a second, differently-worded
  // button commits it (B3).
  const [arming, setArming] = useState<CurationResponse | null>(null)
  const mutation = useMutation({
    mutationFn: (hospitalSlug: string) =>
      sendOffer(token, request.reference, hospitalSlug),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: MEDICATION_CURATION_KEY }),
  })

  // Whether this request can still take an offer. `mutation.isSuccess` is in
  // here on purpose and must stay: `invalidateQueries` is fired, not awaited,
  // so between a successful POST and the refetch landing `request.status` is
  // still 'pending' and `isPending` is already false. That gap is exactly wide
  // enough to send a second offer to the same patient. The server answers the
  // second one 409 not_offerable, but the UI must not let it be attempted.
  const sent = mutation.isSuccess
  const offerable = request.status === 'pending' && !sent

  // Three facts, not the frame's four.
  //
  // TRANSPORT is dropped (M23 gap A2): a medication request has no transport
  // field at all — the line is a copy-paste from the emergency card.
  //
  // CLOSEST PHARMACY is RELABELLED (gap A4). The number is real
  // (`answered_count`) but it counts REPLIES, not proximity; the frame misreads
  // its own data. Padding the grid back to four would mean inventing something.
  //
  // The DENOMINATOR is dropped too (gap A9, found in T08). The old copy read
  // "2 of 10 answered", but nothing asks ten pharmacies: the fan-out lists
  // every active request in EVERY pharmacy queue, there is no asked-set and no
  // count of one, and `answered_count` is `len(responses)`. Ten was the size of
  // the demo fixture. A denominator invites "eight still to come" from a
  // concierge deciding whether to wait.
  const facts: Fact[] = [
    { label: 'AREA', value: request.area ?? 'Not given' },
    { label: 'NEEDED', value: URGENCY_LABEL[request.urgency] ?? request.urgency },
    {
      label: 'RESPONSES',
      value: request.answered_count === 1 ? '1 answer' : `${request.answered_count} answers`,
    },
  ]

  return (
    <RecordCard
      title={`${request.drug_generic_name} ${request.drug_strength} · ${request.quantity}`}
      reference={request.reference}
      age={compactAge(request.created_at)}
      status={sent ? 'offered' : request.status}
      statusLabel={offerable ? 'Awaiting your pick' : 'Offer sent'}
      facts={facts}
    >
      {request.responses.length === 0 ? (
        <div className="mt-2">
          {/* Text-first: M17's rule is that a missing illustration is a reason
              to ship without one, not to invent one. */}
          <EmptyState title="No pharmacy has responded yet">
            Answers usually land within an hour.
          </EmptyState>
        </div>
      ) : (
        <ul className="mt-4 flex flex-col gap-2">
          {request.responses.map((resp) => (
            <li key={resp.hospital_slug}>
              <ResponseRow
                response={resp}
                offerable={offerable}
                busy={mutation.isPending}
                // `offerable` gates the confirm bar as well as the button.
                // Without it the bar survives a successful send — the state
                // that holds WHICH pharmacy was picked is the same state that
                // arms the row, and a test caught exactly that.
                armed={offerable && arming?.hospital_slug === resp.hospital_slug}
                onArm={() => {
                  setArming(resp)
                }}
                onCancel={() => {
                  setArming(null)
                }}
                onSend={() => {
                  mutation.mutate(resp.hospital_slug)
                }}
              />
            </li>
          ))}
        </ul>
      )}

      {/* Already sent — this session, or before the page was opened. Both read
          the same, because both mean the same thing to the concierge: the
          patient has their offer and there is nothing left to pick. */}
      {(sent || request.status !== 'pending') && request.responses.length > 0 && (
        <p className="mt-4 text-sm text-ink-3">
          {sent && arming !== null
            ? `Offer sent to ${arming.hospital_name}. The patient has been texted.`
            : 'An offer has already been sent to this patient.'}
        </p>
      )}

      {mutation.isError && (
        <p role="alert" className="mt-3 text-sm text-bed-bad">
          {mutation.error instanceof ApiError
            ? mutation.error.message
            : 'Could not send the offer.'}
        </p>
      )}
    </RecordCard>
  )
}

/**
 * One pharmacy's answer, as the frame draws it (`1193:8358`): the name, the
 * distance, the price right-aligned, and a **Send as offer** button. The
 * suggested answer's row is tinted and its button solid; the rest are plain
 * with a soft button.
 *
 * The frame's second line reads `3.4km away · Ready in 2hrs`. Only the first
 * half ships (M23 gap A3): `CurationResponse` carries `available`, `price`,
 * `distance_km` and `answered_at` and no readiness estimate at all. A
 * collection time invented from a mockup is a promise made to a patient.
 */
function ResponseRow({
  response,
  offerable,
  busy,
  armed,
  onArm,
  onCancel,
  onSend,
}: {
  response: CurationResponse
  /** Can this request still take an offer at all? */
  offerable: boolean
  /** A send is in flight somewhere on this card. */
  busy: boolean
  /** This row is clicked and awaiting confirmation. */
  armed: boolean
  onArm: () => void
  onCancel: () => void
  onSend: () => void
}): ReactNode {
  const price =
    response.available && response.price_pesewas != null
      ? formatPesewas(response.price_pesewas)
      : null

  return (
    <div
      className={`rounded-xl border px-4 py-3 ${
        response.suggested
          ? 'border-transparent bg-accent-bg'
          : 'border-line-1 bg-bg-app'
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-medium text-ink-1">
            {response.hospital_name}
            {response.suggested && (
              <span className="rounded-full bg-accent px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                Suggested
              </span>
            )}
          </p>
          <p className="mt-0.5 text-[13px] text-ink-3">
            {response.distance_km != null
              ? `${response.distance_km}km away`
              : 'Distance unknown'}
            {' · '}
            {response.city}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <span className="tabular-nums text-sm font-medium text-ink-1">
            {price ?? '—'}
          </span>
          {!response.available ? (
            <span className="inline-flex items-center rounded-full bg-bed-bad-bg px-2 py-0.5 text-xs font-medium text-bed-bad">
              Not available
            </span>
          ) : (
            offerable &&
            !armed && (
              <button
                type="button"
                disabled={busy}
                onClick={onArm}
                className={`inline-flex h-9 items-center rounded-[8px] px-3 text-sm font-medium disabled:opacity-50 ${
                  response.suggested
                    ? 'bg-accent text-white hover:opacity-90'
                    : 'border border-line-2 bg-bg-app text-ink-1 hover:bg-bg-soft'
                }`}
              >
                Send as offer
              </button>
            )
          )}
        </div>
      </div>

      {/* Armed. The consequence is spelled out BEFORE the commit, because the
          commit is irreversible from here: it texts a patient, and the request
          is then closed to further offers. */}
      {armed && (
        <div className="mt-3 flex flex-col gap-3">
          <Callout>
            {`Send ${response.hospital_name}'s ${price ?? 'answer'} to the patient? They get one SMS with this offer, and no other pharmacy's answer can be sent afterwards.`}
          </Callout>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={onSend}
              className="inline-flex h-9 items-center rounded-[8px] bg-accent px-3 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {busy ? 'Sending…' : 'Yes, send this offer'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={onCancel}
              className="inline-flex h-9 items-center rounded-[8px] border border-line-2 bg-bg-app px-3 text-sm font-medium text-ink-1 hover:bg-bg-soft disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
